import { useEffect, useState } from 'react';
import type { RaRecord } from 'react-admin';
import { Link } from 'react-router-dom';
import { Alert, Box, Chip, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import { Row, SectionCard, fmtDate } from '../shared/Panel';
import { fileSizeText } from '../shared/number';
import { classificationLabel } from '../shared/status';
import { sessionToken } from '../session/sessionToken';
import {
  IMPORTANCE_LABELS,
  PARTICIPANT_ROLE_LABELS,
  PARTICIPANT_ROLE_ORDER,
  SCAN_STATUS_LABELS,
  attachmentTree,
  isInlineAttachment,
  participantText,
  participantsByRole,
  participantsOf,
  senderWarning,
  type SenderVerification,
} from './emailStructure';

const API = process.env.REACT_APP_NEST_API_URL || 'http://localhost:3001';

/**
 * Karta maila z pełną strukturą (03.11.2026, przełącznik `emailDetails`,
 * S-03.11-02): uczestnicy z rolami, daty, ważność, znaczniki odpowiedzi
 * automatycznej i wysyłki masowej, ostrzeżenie o nieudanej weryfikacji
 * nadawcy (informacja, nie blokada), wątek z przejściem i załączniki
 * „w treści” / „osobno” z plikami w plikach.
 */

/** Uczestnicy w kolejności ról; UDW tylko wtedy, gdy API je oddało (reguła nadawcy po stronie serwera). */
export const ParticipantRows = ({ record }: { record: RaRecord }) => {
  const list = participantsOf(record);
  return (
    <>
      {PARTICIPANT_ROLE_ORDER.map((role) => {
        const items = participantsByRole(list, role);
        if (items.length === 0 && role !== 'FROM' && role !== 'TO') return null;
        return (
          <Row key={role} label={PARTICIPANT_ROLE_LABELS[role]}>
            <span data-testid={`email-participants-${role.toLowerCase()}`}>
              {items.length ? items.map(participantText).join(', ') : '—'}
            </span>
          </Row>
        );
      })}
    </>
  );
};

export const SenderVerificationAlert = ({ record }: { record: RaRecord }) => {
  const text = senderWarning(record.senderVerification as SenderVerification | undefined);
  if (!text) return null;
  return (
    <Alert severity="warning" data-testid="email-sender-unverified">
      {text}
    </Alert>
  );
};

export const EmailHeaderCard = ({ record, children }: { record: RaRecord; children?: React.ReactNode }) => {
  const importance = record.importance as string | null | undefined;
  const flags: string[] = [];
  if (record.direction === 'outgoing') flags.push('wysłany z naszej skrzynki');
  if (record.isAutoReply) flags.push('odpowiedź automatyczna');
  if (record.isBulk || record.listId) flags.push('wysyłka masowa');
  return (
    <SectionCard title="Wiadomość">
      <Stack spacing={1} data-testid="email-details">
        <SenderVerificationAlert record={record} />
        <Table size="small">
          <TableBody>
            <ParticipantRows record={record} />
            <Row label="Temat">{(record.subject as string) || '—'}</Row>
            <Row label="Wysłano">{fmtDate(record.sentAt as string)}</Row>
            <Row label="Otrzymano">{fmtDate(record.receivedAt as string)}</Row>
            {importance && importance !== 'normal' ? (
              <Row label="Ważność">
                <Chip
                  size="small"
                  color={importance === 'high' ? 'warning' : 'default'}
                  label={IMPORTANCE_LABELS[importance] ?? importance}
                  data-testid="email-importance"
                />
              </Row>
            ) : (
              <></>
            )}
            {flags.length ? (
              <Row label="Rodzaj">
                <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                  {flags.map((f) => (
                    <Chip key={f} size="small" variant="outlined" label={f} />
                  ))}
                </Stack>
              </Row>
            ) : (
              <></>
            )}
            <Row label="Skrzynka">{(record.sourceMailbox as string) || '—'}</Row>
            {children}
          </TableBody>
        </Table>
        {Number(record.hiddenBccCount ?? 0) > 0 && (
          <Typography variant="caption" color="text.secondary">
            Ukryte kopie (UDW) widzi tylko nadawca — nie pokazujemy ich przy mailu przychodzącym.
          </Typography>
        )}
      </Stack>
    </SectionCard>
  );
};

const THREAD_LINK_LABELS: Record<string, string> = {
  thread: 'ten sam wątek',
  conversation: 'ta sama rozmowa w skrzynce',
  headers: 'odpowiedź / przywołanie',
};

/** Wątek: wszystkie maile sprawy z przejściem do każdego. */
export const EmailThreadCard = ({ id }: { id: number }) => {
  const [items, setItems] = useState<RaRecord[] | null>(null);
  useEffect(() => {
    const token = sessionToken();
    fetch(`${API}/emails/${id}/thread`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setItems(d as RaRecord[]))
      .catch(() => setItems([]));
  }, [id]);
  if (items && items.length <= 1) return null;
  return (
    <SectionCard title={items ? `Wątek (${items.length})` : 'Wątek'}>
      {items == null ? (
        <Typography variant="body2" color="text.secondary">
          Ładowanie…
        </Typography>
      ) : (
        <Stack spacing={1} data-testid="email-thread">
          {items.map((m) => {
            const current = (m.id as number) === id;
            return (
              <Box key={m.id as number} data-testid="email-thread-item" data-current={current ? '1' : '0'} sx={{ borderLeft: current ? '3px solid' : '3px solid transparent', borderColor: current ? 'primary.main' : 'transparent', pl: 1 }}>
                {current ? (
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {(m.subject as string) || '(brak tematu)'} — ta wiadomość
                  </Typography>
                ) : (
                  <Typography variant="body2" component={Link} to={`/emails/${m.id as number}/show`} data-testid="email-thread-link">
                    {(m.subject as string) || '(brak tematu)'}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {m.direction === 'outgoing' ? 'od nas · ' : ''}
                  {(m.fromName as string) || (m.fromAddress as string) || '—'} · {fmtDate((m.sentAt as string) || (m.receivedAt as string))}
                  {m.classification ? ` · ${classificationLabel(m.classification as string)}` : ''}
                  {!current && m.threadLink ? ` · ${THREAD_LINK_LABELS[m.threadLink as string] ?? ''}` : ''}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      )}
    </SectionCard>
  );
};

/** Załączniki: w treści / osobno, pliki z wnętrza innych plików wcięte pod nimi. */
export const EmailAttachmentsCard = ({ record }: { record: RaRecord }) => {
  const attachments = (record.attachments as RaRecord[] | undefined) ?? [];
  const html = record.bodyHtml as string | null;
  const tree = attachmentTree(attachments);
  return (
    <SectionCard title={`Załączniki (${attachments.length})`}>
      {attachments.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          brak
        </Typography>
      ) : (
        <Table size="small" data-testid="email-attachments">
          <TableBody>
            {tree.map(({ att: a, depth }) => {
              const inline = isInlineAttachment(a, html);
              const scan = a.scanStatus as string | null | undefined;
              return (
                <TableRow key={a.id as number} data-testid="email-attachment-row" data-depth={depth} data-inline={inline ? '1' : '0'}>
                  <TableCell sx={{ border: 0, pl: 1 + depth * 3 }}>
                    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                      {depth > 0 && <SubdirectoryArrowRightIcon fontSize="small" color="action" />}
                      <span>{a.filename as string}</span>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ border: 0 }}>
                    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                      <Chip size="small" variant="outlined" label={inline ? 'w treści' : depth > 0 ? 'w załączniku' : 'osobny plik'} />
                      {a.pageCount ? <Chip size="small" variant="outlined" label={`stron: ${a.pageCount as number}`} /> : null}
                      {scan === 'infected' ? (
                        <Chip size="small" color="error" label={SCAN_STATUS_LABELS.infected} data-testid="email-attachment-infected" />
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ border: 0, color: 'text.secondary' }}>{(a.detectedType as string) || (a.contentType as string)}</TableCell>
                  <TableCell sx={{ border: 0 }} align="right">
                    {fileSizeText(a.size as number)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </SectionCard>
  );
};
